import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { ITermFormValues } from "containers/TermFormContainer";
import { downloadApiExport, getDateForFileName } from "helpers/utils";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { DTOTermPeriodData, ITerm } from "store/managementStore";

import type { IAdvanceSlice } from "./advancePaymentSlice";
import { deleteByTerm as beneficiaryDeleteByTerm } from "./beneficiaryStore";
import { deleteByTerm as titleDeleteByTerm } from "./titleStore";
import { removeWorkTerm } from "./workStore";
import type { PeriodStatus, TFetchStatus } from "./types";

export interface ITermAdvancePayment {
    id: number;
    beneficiar: string;
    title: string;
}

export const fetchTerms = createAsyncThunk("terms/terms", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/advancePayment`);
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchTerm = createAsyncThunk("terms/term", async (id: number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${id}`);
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const createTerm = createAsyncThunk(
    "terms/createTerm",
    async ({ data }: { data: ITermFormValues }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/term`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteTerm = createAsyncThunk("terms/deleteTerm", async (id: number, { dispatch, rejectWithValue }) => {
    try {
        const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${id}`);
        if (response.status === 200) {
            dispatch(titleDeleteByTerm(id));
            dispatch(beneficiaryDeleteByTerm(id));
            dispatch(removeWorkTerm(id));
        } else {
            throw Error;
        }
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const editTerm = createAsyncThunk(
    "terms/editTerm",
    async ({ data, id }: { data: any; id: string }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${id}`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const checkIfContractAlreadyCreated = createAsyncThunk(
    "terms/checkIfContractAlreadyCreated",
    async (
        {
            titleId,
            beneficiarId,
            representativeId,
        }: { titleId: string; beneficiarId: string; representativeId: string },
        { rejectWithValue },
    ) => {
        const data = {
            titleId,
            beneficiarId,
            representativeId,
        };
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/term/check`, data);
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const uploadDocument = createAsyncThunk(
    "bpTerms/uploadDocument",
    async ({ id, file }: { id: number; file: FormData }) => {
        try {
            const config = {
                headers: {
                    "content-type": "multipart/form-data",
                },
            };
            const response = await toast.promise(
                axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${id}/agreement`, file),
                {
                    pending: `${t("Upload.Pending")}`,
                    success: `${t("Upload.Successful")}`,
                    error: `${t("Upload.Error")}`,
                },
            );
            return response.data.data;
        } catch (error: any) {
            console.log(error);
        }
    },
);

export const deleteTermDocument = createAsyncThunk(
    "terms/deleteTermDocument",
    async ({ termId }: { termId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/agreement`);
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportTermList = createAsyncThunk("terms/export", async (_, { rejectWithValue }) => {
    try {
        const response = await toast.promise(
            axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/export`, {
                headers: { Accept: "*/*" },
            }),
            {
                pending: `${t("Export.Pending")}`,
                success: `${t("Export.Successful")}`,
                error: `${t("Export.Error")}`,
            },
        );

        const fileName = `export_term_${getDateForFileName()}.csv`;
        downloadApiExport(response.data, fileName);
    } catch (error: any) {
        return rejectWithValue(error);
    }
});

export const sendManualContractBook = createAsyncThunk(
    "beneficiaries/sendPicture",
    async (
        { termId, file, signatureDate }: { termId: number; file: FormData; signatureDate: Date },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/agreement`,
                file,
                {
                    params: { signatureDate },
                },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
/**
 * Fetches all advance payments linked to a term.
 * If onlyAdvances is true, it will exclude forfaits.
 * If typeOfAdvance is not "all", it will return slices either disconnected or connected to a TPD.
 */
export const fetchAdvancePaymentsOfTerm = createAsyncThunk(
    "terms/fetchAdvancePaymentsOfTerm",
    async (
        {
            termId,
            typeOfPayment = "all",
            typeOfAdvance = "all",
        }: {
            termId: number;
            typeOfPayment?: "all" | "advance" | "package";
            typeOfAdvance?: "all" | "disconnected" | "connected";
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/advance`, {
                params: { typeOfPayment, typeOfAdvance },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const renewTerm = createAsyncThunk(
    "terms/renew",
    async ({ termId, endDate }: { termId: number; endDate: string }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/renew`, {
                endDate,
            });
            return response.data;
        } catch (error: any) {
            // Network errors have no response; let the rejected case fall back to the generic message.
            return rejectWithValue(error.response?.data);
        }
    },
);

export const getTPDLinkedToTerm = createAsyncThunk(
    "terms/getTPDLinkedToTerm",
    async (
        { termId, latest = false, periodStatus }: { termId: number; latest?: boolean; periodStatus?: PeriodStatus[] },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/tpd`, {
                params: {
                    latest,
                    ...(periodStatus?.length ? { periodStatus } : {}),
                },
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type TermPeriodDataImpactSummary = DTOTermPeriodData & {
    originalPeriod: number;
    periodStart?: string;
    periodEnd?: string;
};

export const getTermPeriodDataById = createAsyncThunk(
    "terms/getTermPeriodDataById",
    async ({ tpdId }: { tpdId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/tpd/${tpdId}`);
            return response.data.data as TermPeriodDataImpactSummary;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data);
        }
    },
);

export const addProvisions = createAsyncThunk(
    "terms/addProvisions",
    async (
        data: {
            termId?: number | number[];
            percent?: number;
            periodId: number;
            reinstatePercent?: number;
            termIdReinstate?: number | number[];
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + "/term/provision", {
                ...(data.termId !== undefined && { termId: data.termId }),
                ...(data.termIdReinstate !== undefined && { reinstateTermId: data.termIdReinstate }),
                ...(data.percent !== undefined && { percent: data.percent }),
                ...(data.reinstatePercent !== undefined && { reinstatePercent: data.reinstatePercent }),
                periodId: data.periodId,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchPreviousInitialProvision = createAsyncThunk(
    "terms/fetchPreviousInitialProvision",
    async ({ termId, periodId }: { termId: number; periodId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/previous-provision`,
                { params: { periodId } },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface TermState {
    term: ITerm;
    terms: ITermAdvancePayment[];
    termStatus: TFetchStatus;
    termsStatus: TFetchStatus;
    contractAlreadyCreated: boolean | null;
    contractAlreadyCreatedPending: boolean;
    termAdvancePayments: IAdvanceSlice[];
    deleteTermDocumentStatus: TFetchStatus;
    termAdvanceStatus: TFetchStatus;
    termTpd: DTOTermPeriodData[];
    TPDFetchStatus: TFetchStatus;
    addProvisionsStatus: TFetchStatus;
    previousInitialProvisionStatus: TFetchStatus;
}

const initialState: TermState = {
    term: {} as ITerm,
    terms: [],
    termStatus: "idle",
    termsStatus: "idle",
    contractAlreadyCreated: null,
    contractAlreadyCreatedPending: false,
    termAdvancePayments: [],
    deleteTermDocumentStatus: "idle",
    termAdvanceStatus: "idle",
    termTpd: [],
    TPDFetchStatus: "idle",
    addProvisionsStatus: "idle",
    previousInitialProvisionStatus: "idle",
};

export const termsSlice = createSlice({
    name: "beneficiars",
    initialState,
    reducers: {
        clearCurrentTerm(state) {
            state.term = {} as ITerm;
        },
        resetTermStatus(state) {
            state.termStatus = "idle";
        },
    },
    extraReducers: (builder) => {
        // Check if contract created
        builder.addCase(checkIfContractAlreadyCreated.pending, (state) => {
            state.contractAlreadyCreatedPending = true;
            state.contractAlreadyCreated = null;
        });
        builder.addCase(checkIfContractAlreadyCreated.fulfilled, (state, action) => {
            state.contractAlreadyCreatedPending = false;
            state.contractAlreadyCreated = action.payload;
        });
        // Fetch terms
        builder.addCase(fetchTerms.fulfilled, (state, action) => {
            state.terms = action.payload;
            state.termsStatus = "succeeded";
        });
        builder.addCase(fetchTerms.rejected, (state, { payload }) => {
            state.termsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTerms.pending, (state) => {
            state.termsStatus = "pending";
        });
        // Fetch one term
        builder.addCase(fetchTerm.fulfilled, (state, action) => {
            state.term = action.payload;
            state.termStatus = "succeeded";
        });
        builder.addCase(fetchTerm.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.termStatus = "failed";
        });
        builder.addCase(fetchTerm.pending, (state) => {
            state.termStatus = "pending";
        });

        // Create a term
        builder.addCase(createTerm.fulfilled, () => {
            toast.success(`${t("ConnectTitle.ConnectTitleSuccess")}`);
        });

        builder.addCase(createTerm.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { autoClose: 5000 });
        });
        // Delete one term
        builder.addCase(deleteTerm.pending, () => {});
        builder.addCase(deleteTerm.fulfilled, () => {
            toast.success(`${t("Terms.DeleteTermSuccess")}`);
        });
        builder.addCase(deleteTerm.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        // Edit a term
        builder.addCase(editTerm.pending, () => {});
        builder.addCase(editTerm.fulfilled, () => {
            toast.success(`${t("ConnectBeneficiar.EditTermSuccess")}`);
        });
        builder.addCase(editTerm.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        // Upload a document
        builder.addCase(uploadDocument.fulfilled, (state, action) => {
            if (state.term) {
                (state.term as ITerm).originalFilename = action.payload.originalFilename;
                (state.term as ITerm).originalFilemime = action.payload.originalFilemime;
                (state.term as ITerm).originalFilesize = action.payload.originalFilesize;
            }
        });
        // Delete uploaded document
        builder.addCase(deleteTermDocument.pending, (state) => {
            state.deleteTermDocumentStatus = "pending";
        });
        builder.addCase(deleteTermDocument.fulfilled, (state) => {
            state.term.originalFilename = null;
            state.term.originalFilemime = null;
            state.term.originalFilesize = null;
            toast.success(`${t("Terms.PDFDeletedSuccessfully")}`);
            state.deleteTermDocumentStatus = "succeeded";
        });
        builder.addCase(deleteTermDocument.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.deleteTermDocumentStatus = "failed";
        });
        builder.addCase(fetchAdvancePaymentsOfTerm.fulfilled, (state, { payload }) => {
            state.termAdvancePayments = payload.data;
            state.termAdvanceStatus = "fulfilled";
        });
        builder.addCase(fetchAdvancePaymentsOfTerm.rejected, (state, { payload }) => {
            state.termAdvancePayments = [];
            state.termAdvanceStatus = "rejected";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(renewTerm.fulfilled, () => {
            toast.success(`${t("Terms.RenewSuccessfully")}`);
        });
        builder.addCase(renewTerm.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(getTPDLinkedToTerm.fulfilled, (state, { payload }) => {
            state.termTpd = [payload].flat();
            state.TPDFetchStatus = "fulfilled";
        });
        builder.addCase(getTPDLinkedToTerm.pending, (state) => {
            state.TPDFetchStatus = "pending";
            state.termTpd = [];
        });
        builder.addCase(getTPDLinkedToTerm.rejected, (state, { payload }) => {
            state.TPDFetchStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(addProvisions.pending, (state) => {
            state.addProvisionsStatus = "pending";
        });
        builder.addCase(addProvisions.fulfilled, (state) => {
            toast.success(`${t("Management.AddProvisionsSuccess")}`);
            state.addProvisionsStatus = "succeeded";
        });
        builder.addCase(addProvisions.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.addProvisionsStatus = "failed";
        });
        builder.addCase(fetchPreviousInitialProvision.pending, (state) => {
            state.previousInitialProvisionStatus = "pending";
        });
        builder.addCase(fetchPreviousInitialProvision.fulfilled, (state) => {
            state.previousInitialProvisionStatus = "succeeded";
        });
        builder.addCase(fetchPreviousInitialProvision.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.previousInitialProvisionStatus = "failed";
        });
    },
});

export const { clearCurrentTerm, resetTermStatus } = termsSlice.actions;

export default termsSlice.reducer;
